import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error - iOS specific property
      window.navigator.standalone === true;

    setIsStandalone(standalone);
    if (standalone) return;

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
    setIsIOS(iOS);

    // Check if user has dismissed before
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed) return;

    // Listen for beforeinstallprompt (Chrome/Android)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show after a small delay to not interrupt initial experience
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show after a delay
    if (iOS) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  // Don't show if already installed or dismissed
  if (isStandalone || !showPrompt) return null;

  return (
    <div className="install-prompt">
      {isIOS ? (
        <>
          <h3>Install Read Later</h3>
          <p>
            Add this app to your home screen for the best experience.
            Tap <span className="share-icon" style={{ fontSize: '1.2em' }}>⬆️</span> then
            select "Add to Home Screen".
          </p>
          <div className="actions">
            <button className="secondary" onClick={handleDismiss}>
              Maybe Later
            </button>
          </div>
        </>
      ) : (
        <>
          <h3>Install Read Later</h3>
          <p>
            Get quick access from your home screen with offline support
            and a native app experience.
          </p>
          <div className="actions">
            <button className="secondary" onClick={handleDismiss}>
              Not Now
            </button>
            <button onClick={handleInstall}>
              Install App
            </button>
          </div>
        </>
      )}
    </div>
  );
}
